// Common aspect ratios used across video, photo, web, and print.

export interface AspectRatio {
  key: string;
  w: number;
  h: number;
  label: string;     // "16:9"
  decimal: number;   // w/h
  name: { en: string; bn: string };
  uses: { en: string; bn: string };
}

function build(w: number, h: number, name: { en: string; bn: string }, uses: { en: string; bn: string }, label?: string): AspectRatio {
  return {
    key: `${w}x${h}`,
    w, h,
    label: label ?? `${w}:${h}`,
    decimal: w / h,
    name, uses,
  };
}

export const COMMON_RATIOS: AspectRatio[] = [
  build(16, 9, { en: "HDTV / Widescreen", bn: "এইচডিটিভি / ওয়াইডস্ক্রিন" },
    { en: "YouTube, modern TVs and monitors, Full HD, 4K", bn: "ইউটিউব, আধুনিক টিভি ও মনিটর, ফুল এইচডি, ৪কে" }),
  build(9, 16, { en: "Mobile portrait", bn: "মোবাইল পোর্ট্রেট" },
    { en: "TikTok, Instagram Reels, Facebook & YouTube Shorts, Stories", bn: "টিকটক, ইনস্টাগ্রাম রিল্‌স, ফেসবুক ও ইউটিউব শর্টস, স্টোরি" }),
  build(4, 3, { en: "Standard / Old TV", bn: "স্ট্যান্ডার্ড / পুরনো টিভি" },
    { en: "Old CRT TVs, iPad standard, projectors, presentations", bn: "পুরনো সিআরটি টিভি, আইপ্যাড, প্রজেক্টর, প্রেজেন্টেশন" }),
  build(3, 4, { en: "Portrait standard", bn: "পোর্ট্রেট স্ট্যান্ডার্ড" },
    { en: "Pinterest portrait, mobile photo prints", bn: "পিন্টারেস্ট পোর্ট্রেট, মোবাইল ছবি প্রিন্ট" }),
  build(1, 1, { en: "Square", bn: "বর্গ" },
    { en: "Instagram square posts, profile pictures, album covers", bn: "ইনস্টাগ্রাম স্কয়ার পোস্ট, প্রোফাইল পিকচার, অ্যালবাম কভার" }),
  build(21, 9, { en: "Ultrawide cinema", bn: "আল্ট্রাওয়াইড সিনেমা" },
    { en: "Ultrawide gaming monitors, cinematic videos", bn: "আল্ট্রাওয়াইড গেমিং মনিটর, সিনেমাটিক ভিডিও" }),
  build(3, 2, { en: "DSLR / 35mm", bn: "ডিএসএলআর / ৩৫মিমি" },
    { en: "DSLR & mirrorless full-frame cameras, classic film", bn: "ডিএসএলআর ও মিররলেস ফুল-ফ্রেম ক্যামেরা, ক্ল্যাসিক ফিল্ম" }),
  build(2, 3, { en: "DSLR portrait", bn: "ডিএসএলআর পোর্ট্রেট" },
    { en: "Vertical DSLR shots, 4×6 prints", bn: "ভার্টিক্যাল ডিএসএলআর শট, ৪×৬ প্রিন্ট" }),
  build(5, 4, { en: "Old monitor", bn: "পুরনো মনিটর" },
    { en: "Old 17–19″ LCD monitors (1280×1024)", bn: "পুরনো ১৭–১৯″ এলসিডি মনিটর (১২৮০×১০২৪)" }),
  build(4, 5, { en: "IG portrait", bn: "ইনস্টাগ্রাম পোর্ট্রেট" },
    { en: "Instagram portrait posts (1080×1350)", bn: "ইনস্টাগ্রাম পোর্ট্রেট পোস্ট (১০৮০×১৩৫০)" }),
  build(185, 100, { en: "Standard cinema", bn: "স্ট্যান্ডার্ড সিনেমা" },
    { en: "Most modern theatrical movies", bn: "অধিকাংশ আধুনিক সিনেমা" }, "1.85:1"),
  build(235, 100, { en: "CinemaScope", bn: "সিনেমাস্কোপ" },
    { en: "Classic cinematic widescreen", bn: "ক্ল্যাসিক সিনেমাটিক ওয়াইডস্ক্রিন" }, "2.35:1"),
  build(239, 100, { en: "Anamorphic widescreen", bn: "অ্যানামরফিক ওয়াইডস্ক্রিন" },
    { en: "Modern anamorphic Hollywood films", bn: "আধুনিক হলিউড অ্যানামরফিক চলচ্চিত্র" }, "2.39:1"),
];

export interface SocialPreset {
  key: string;
  platform: string;
  type: { en: string; bn: string };
  w: number;
  h: number;
  ratio: string;
}

export const SOCIAL_PRESETS: SocialPreset[] = [
  { key: "yt-thumb",  platform: "YouTube",   type: { en: "Thumbnail",       bn: "থাম্বনেইল" },        w: 1280, h: 720,  ratio: "16:9" },
  { key: "yt-video",  platform: "YouTube",   type: { en: "Video (Full HD)", bn: "ভিডিও (ফুল এইচডি)" }, w: 1920, h: 1080, ratio: "16:9" },
  { key: "yt-short",  platform: "YouTube",   type: { en: "Short",           bn: "শর্ট" },              w: 1080, h: 1920, ratio: "9:16" },
  { key: "ig-square", platform: "Instagram", type: { en: "Square post",     bn: "স্কয়ার পোস্ট" },     w: 1080, h: 1080, ratio: "1:1" },
  { key: "ig-port",   platform: "Instagram", type: { en: "Portrait post",   bn: "পোর্ট্রেট পোস্ট" },   w: 1080, h: 1350, ratio: "4:5" },
  { key: "ig-story",  platform: "Instagram", type: { en: "Story / Reel",    bn: "স্টোরি / রিল" },     w: 1080, h: 1920, ratio: "9:16" },
  { key: "fb-cover",  platform: "Facebook",  type: { en: "Page cover",      bn: "পেজ কভার" },          w: 820,  h: 312,  ratio: "~2.63:1" },
  { key: "fb-post",   platform: "Facebook",  type: { en: "Feed post",       bn: "ফিড পোস্ট" },         w: 1200, h: 630,  ratio: "1.91:1" },
  { key: "fb-story",  platform: "Facebook",  type: { en: "Story",           bn: "স্টোরি" },            w: 1080, h: 1920, ratio: "9:16" },
  { key: "tw-post",   platform: "X / Twitter", type: { en: "Image post",    bn: "ইমেজ পোস্ট" },        w: 1200, h: 675,  ratio: "16:9" },
  { key: "tt-vid",    platform: "TikTok",    type: { en: "Video",           bn: "ভিডিও" },             w: 1080, h: 1920, ratio: "9:16" },
  { key: "li-post",   platform: "LinkedIn",  type: { en: "Image post",      bn: "ইমেজ পোস্ট" },        w: 1200, h: 627,  ratio: "1.91:1" },
];

export interface PrintPreset {
  inches: string;
  ratio: string;
  decimal: number;
  use: { en: string; bn: string };
}

export const PRINT_SIZES: PrintPreset[] = [
  { inches: "4×6",  ratio: "3:2",  decimal: 1.5,    use: { en: "Standard photo print",  bn: "স্ট্যান্ডার্ড ফটো প্রিন্ট" } },
  { inches: "5×7",  ratio: "7:5",  decimal: 1.4,    use: { en: "Frame photo",           bn: "ফ্রেম ফটো" } },
  { inches: "8×10", ratio: "5:4",  decimal: 1.25,   use: { en: "Portrait, certificate", bn: "পোর্ট্রেট, সার্টিফিকেট" } },
  { inches: "8×12", ratio: "3:2",  decimal: 1.5,    use: { en: "Enlarged DSLR print",   bn: "বড় ডিএসএলআর প্রিন্ট" } },
  { inches: "11×14",ratio: "11:14",decimal: 0.7857, use: { en: "Wall portrait",         bn: "ওয়াল পোর্ট্রেট" } },
];
